import { generatePath, matchPath } from 'react-router-dom'
import { IOveradvance } from '@common/interfaces/bbc'
import { ClientInfoStatus, IClientInfo, OveradvanceStatus } from '@common/interfaces/client'
import { IResponse, IFileDownloadResponse } from '@common/interfaces/request'
import { UserRole } from '@common/interfaces/user'
import moment from 'moment'
import { Action } from 'redux'
import { takeEvery, select, put, call } from 'redux-saga/effects'
import { putPromise } from 'src/helpers/redux'
import { IApi } from '../../api'
import { ROUTES } from '../../constants/routes'
import { download } from '../../helpers/helpers'
import { sagaNavigate } from '../navigationHelper'
import { reloadClientInfo } from '../profile/actions'
import { role } from '../profile/selectors'
import { listRequiredReports } from '../requiredReport/actions'
import {
  UPDATE_SUCCESS,
  EXPORT_ACCOUNT_ACTIVITY_SUCCESS,
  EXPORT_ACCOUNT_ACTIVITY_FAILURE,
  EXPORT_CLIENT_FEE_STATEMENT_SUCCESS,
  EXPORT_CLIENT_FEE_STATEMENT_FAILURE,
  CREATE_CLIENT_OVERADVANCE_SUCCESS,
  UPDATE_CLIENT_OVERADVANCE_SUCCESS,
  REORDER_OVERADVANCES_SUCCESS,
  DELETE_CLIENT_OVERADVANCE_SUCCESS,
  CREATE_REQUIRED_REPORTS_SUCCESS,
  REQUEST_FUNDING_SUCCESS,
  MODIFY_FUNDING_SUCCESS,
  CANCEL_FUNDING_SUCCESS,
  TERMINATE_SUCCESS,
  SUBMIT_ONGOING_REPORTING_SUCCESS,
  listOveradvances,
  show,
  EXPORT_PORTFOLIO_REVIEW_REQUEST,
  EXPORT_PORTFOLIO_REVIEW_FAILURE,
  EXPORT_PORTFOLIO_REVIEW_SUCCESS,
  EXPORT_TERM_LOANS_OVERADVANCES_REQUEST,
  EXPORT_TERM_LOANS_OVERADVANCES_FAILURE,
  EXPORT_TERM_LOANS_OVERADVANCES_SUCCESS,
  UPDATE_TERMS_SUCCESS,
  EXPORT_MASTER_INVENTORY_SUCCESS,
  EXPORT_MASTER_INVENTORY_FAILURE,
  EXPORT_RISK_RATING_DETAILS_SUCCESS,
  EXPORT_RISK_RATING_DETAILS_FAILURE,
  EXPORT_AR_ELIGIBILITY_FAILURE,
  EXPORT_AR_ELIGIBILITY_SUCCESS,
} from './actions'
import { clientInfo } from './selectors'

export function* updateSuccess() {
  yield takeEvery(UPDATE_SUCCESS, function* () {
    const { id: clientId } = (yield select(clientInfo)) as { id: string }
    const path = window.location.pathname
    if (matchPath({ path: ROUTES.CLIENT_PAGE }, path)) {
      yield putPromise(listRequiredReports(clientId))
    }
  })
}

export function* exportClientAccountActivity() {
  yield takeEvery(
    EXPORT_ACCOUNT_ACTIVITY_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        yield download(action.data, action.data.name)
      } catch (error: unknown) {
        yield put({ type: EXPORT_ACCOUNT_ACTIVITY_FAILURE, error })
      }
    },
  )
}

export function* exportClientFeeStatement() {
  yield takeEvery(
    EXPORT_CLIENT_FEE_STATEMENT_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        yield download(action.data, action.data.name)
      } catch (error: unknown) {
        yield put({ type: EXPORT_CLIENT_FEE_STATEMENT_FAILURE, error })
      }
    },
  )
}

export function* exportArEligibility() {
  yield takeEvery(
    EXPORT_AR_ELIGIBILITY_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        yield download(action.data, action.data.name)
      } catch (error: unknown) {
        yield put({ type: EXPORT_AR_ELIGIBILITY_FAILURE, error })
      }
    },
  )
}

export function* loadOveradvances() {
  yield takeEvery(
    [
      CREATE_CLIENT_OVERADVANCE_SUCCESS,
      UPDATE_CLIENT_OVERADVANCE_SUCCESS,
      REORDER_OVERADVANCES_SUCCESS,
      DELETE_CLIENT_OVERADVANCE_SUCCESS,
    ],
    function* (action: IResponse<IOveradvance> & Action) {
      // Skip the auto-refresh ONLY when a Draft overadvance was just created
      // via the amendment-analysis flow — the component immediately chains a
      // `createAnalysis` request and navigates away, so a concurrent
      // `listOveradvances` only contends for connections and slows the
      // chain (the term-loan flow has no such saga and works correctly).
      // Updates of an existing Draft (analyst tweaking proposed values)
      // still need the refresh so the form's `initialValues` re-sync with
      // the persisted state — otherwise `dirty`/`isChanged` stays true and
      // the RouteLeavingGuard blocks navigation.
      const isCreate = action.type === CREATE_CLIENT_OVERADVANCE_SUCCESS
      if (isCreate && action.data?.status === OveradvanceStatus.Draft) {
        return
      }
      const info: IClientInfo = yield select(clientInfo)
      const { id: clientId } = info

      yield putPromise(listOveradvances(clientId))
    },
  )
}

export function* createRequireReportSuccess() {
  yield takeEvery(CREATE_REQUIRED_REPORTS_SUCCESS, function* () {
    yield putPromise(reloadClientInfo())
  })
}

export function* repullClientInfo() {
  yield takeEvery(
    [
      REQUEST_FUNDING_SUCCESS,
      SUBMIT_ONGOING_REPORTING_SUCCESS,
      MODIFY_FUNDING_SUCCESS,
      CANCEL_FUNDING_SUCCESS,
      UPDATE_TERMS_SUCCESS,
    ],
    function* () {
      const userRole = (yield select(role)) as UserRole
      if (userRole === UserRole.CLIENT_USER) {
        yield putPromise(reloadClientInfo())
      } else {
        const info = (yield select(clientInfo)) as IClientInfo
        yield putPromise(show(info.id))
      }
    },
  )
}

export function* terminateSuccess() {
  yield takeEvery(
    TERMINATE_SUCCESS,
    function* (data: IResponse<{ clientStatus: ClientInfoStatus }> & Action) {
      const { id: clientId }: { id: string } = yield select(clientInfo)
      if (data.data.clientStatus === ClientInfoStatus.Past) {
        yield call(sagaNavigate, generatePath(ROUTES.CLIENT_PAGE, { id: clientId }))
      }
    },
  )
}

export function* exportPortfolioReview(api: IApi) {
  yield takeEvery(EXPORT_PORTFOLIO_REVIEW_REQUEST, function* () {
    try {
      const userRole = (yield select(role)) as UserRole
      let data: Blob
      if (userRole === UserRole.PARTICIPANT_USER) {
        data = (yield api.clientInfo.exportParticipantPortfolioReview()) as Blob
      } else {
        data = (yield api.clientInfo.exportPortfolioReview()) as Blob
      }
      const date = moment().format('YYYY-MM-DD')

      download(data, `Portfolio Review as of ${date}.xlsx`)

      yield put({ type: EXPORT_PORTFOLIO_REVIEW_SUCCESS })
    } catch (error: unknown) {
      yield put({ type: EXPORT_PORTFOLIO_REVIEW_FAILURE, error: error as Error })
    }
  })
}

export function* exportTermLoansAndOveradvances(api: IApi) {
  yield takeEvery(
    EXPORT_TERM_LOANS_OVERADVANCES_REQUEST,
    function* (action: { type: string; params?: object }) {
      try {
        const data: Blob = yield api.clientInfo.exportTermLoansAndOveradvances(action.params)
        const date = moment().format('YYYY-MM-DD')

        download(data, `Term Loans & Overadvances as of ${date}.xlsx`)

        yield put({ type: EXPORT_TERM_LOANS_OVERADVANCES_SUCCESS })
      } catch (error: unknown) {
        yield put({ type: EXPORT_TERM_LOANS_OVERADVANCES_FAILURE, error: error as Error })
      }
    },
  )
}

export function* exportRiskRatingSummary() {
  yield takeEvery(
    EXPORT_RISK_RATING_DETAILS_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        yield download(action.data, action.data.name)
      } catch (error: unknown) {
        yield put({ type: EXPORT_RISK_RATING_DETAILS_FAILURE, error })
      }
    },
  )
}

export function* exportMasterInventory() {
  yield takeEvery(
    EXPORT_MASTER_INVENTORY_SUCCESS,
    function* (action: IFileDownloadResponse & Action) {
      try {
        yield download(action.data, action.data.name)
      } catch (error: unknown) {
        yield put({ type: EXPORT_MASTER_INVENTORY_FAILURE, error })
      }
    },
  )
}
